import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Phase3Disclaimer } from '@/components/governance/Phase3Disclaimer';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
  ShieldCheck,
  Loader2,
  FileSearch,
  Pill,
} from 'lucide-react';
import type { IntraOpResult } from '@/types/intraOpEvidence';
import { cn } from '@/lib/utils';

// --- Demo payload for development / preview ---
const DEMO_RESULT: IntraOpResult = {
  primary_cpt: {
    code: '29888',
    description: 'Arthroscopically aided anterior cruciate ligament repair/augmentation or reconstruction',
    matches_preop_auth: true,
  },
  additional_cpts: [
    { code: '29881', description: 'Arthroscopy, knee, surgical; with meniscectomy', type: 'add-on' },
    { code: '29877', description: 'Arthroscopy, knee, surgical; debridement/shaving of articular cartilage', type: 'standalone' },
    { code: 'G0289', description: 'Arthroscopy, knee, surgical, for removal of loose body', type: 'hcpcs' },
  ],
  icd10_codes: [
    { code: 'S83.511A', description: 'Sprain of anterior cruciate ligament of right knee, initial encounter' },
    { code: 'M23.211', description: 'Derangement of anterior horn of medial meniscus, right knee' },
    { code: 'M17.11', description: 'Primary osteoarthritis, right knee' },
  ],
  modifiers: [
    { code: '59', description: 'Distinct Procedural Service', rationale: 'Meniscectomy performed on a separate compartment from the ACL reconstruction site, constituting a distinct procedural service per NCCI guidelines.' },
    { code: 'RT', description: 'Right Side', rationale: 'All procedures performed on the right knee as documented in the operative note header and body.' },
  ],
  documentation_gaps: [
    'Graft source (autograft vs. allograft) not explicitly stated in operative body.',
    'Tourniquet time not documented — required by some payers for arthroscopic procedures > 60 min.',
    'No mention of intraoperative imaging confirmation (fluoroscopy).',
  ],
  suggested_additions_to_note:
    'The ACL reconstruction was performed using a [autograft/allograft] graft. Tourniquet was inflated for [XX] minutes at [XXX] mmHg. Intraoperative fluoroscopic confirmation of tunnel placement was [performed/not required].',
  confidence_score: 78,
  confidence_level: 'MEDIUM',
};

export function IntraOpEvidenceAnalyzer() {
  const [noteText, setNoteText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<IntraOpResult | null>(null);

  const handleAnalyze = useCallback(() => {
    if (!noteText.trim()) return;
    setIsAnalyzing(true);
    setResult(null);

    // Simulate AI call — replace with actual edge function call
    setTimeout(() => {
      setResult(DEMO_RESULT);
      setIsAnalyzing(false);
    }, 2800);
  }, [noteText]);

  const handleAppendSuggestion = useCallback(() => {
    if (!result?.suggested_additions_to_note) return;
    setNoteText((prev) => prev + '\n\n' + result.suggested_additions_to_note);
  }, [result]);

  const confidenceColor =
    result?.confidence_level === 'HIGH'
      ? 'bg-success'
      : result?.confidence_level === 'MEDIUM'
        ? 'bg-warning'
        : 'bg-destructive';

  const confidenceTextColor =
    result?.confidence_level === 'HIGH'
      ? 'text-success'
      : result?.confidence_level === 'MEDIUM'
        ? 'text-warning'
        : 'text-destructive';

  return (
    <div className="space-y-6">
      {/* --- INPUT AREA --- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSearch className="h-5 w-5 text-primary" />
            Operative Note / Dictation Input
          </CardTitle>
          <CardDescription>
            Paste the full operative note or dictation transcript below for PRI integrity analysis.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Paste operative note or dictation transcript here..."
            className="min-h-[200px] font-mono text-sm leading-relaxed"
          />
          <Button
            onClick={handleAnalyze}
            disabled={isAnalyzing || !noteText.trim()}
            className="w-full sm:w-auto"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing…
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4" />
                Run PRI Integrity Check
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* --- LOADING STATE --- */}
      {isAnalyzing && (
        <Card className="border-primary/30">
          <CardContent className="py-8 space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm font-medium text-muted-foreground">
                AAPC-Certified AI parsing CPTs and NCCI edits…
              </span>
            </div>
            <div className="space-y-3 max-w-xl mx-auto">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/6" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* --- RESULTS DASHBOARD --- */}
      {result && !isAnalyzing && (
        <div className="space-y-5">
          {/* Primary Procedure Card */}
          <Card className={cn(
            'border-2',
            result.primary_cpt.matches_preop_auth
              ? 'border-success/40 bg-success/5'
              : 'border-destructive/40 bg-destructive/5'
          )}>
            <CardContent className="py-5">
              <div className="flex items-start gap-4">
                {result.primary_cpt.matches_preop_auth ? (
                  <CheckCircle2 className="h-8 w-8 text-success flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-8 w-8 text-destructive flex-shrink-0 mt-0.5" />
                )}
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Primary Procedure
                  </p>
                  <p className="text-2xl font-bold font-mono">{result.primary_cpt.code}</p>
                  <p className="text-sm text-foreground/80">{result.primary_cpt.description}</p>
                  <p className={cn(
                    'text-xs font-medium mt-1',
                    result.primary_cpt.matches_preop_auth ? 'text-success' : 'text-destructive'
                  )}>
                    {result.primary_cpt.matches_preop_auth
                      ? '✓ Matches Pre-Op Authorization'
                      : '✗ Does NOT Match Pre-Op Authorization'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Add-On Codes & HCPCS */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Add-On Codes & HCPCS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border">
                {result.additional_cpts.map((cpt) => (
                  <div key={cpt.code} className="flex items-center gap-3 py-2.5">
                    <span className="font-mono text-sm font-semibold w-16">{cpt.code}</span>
                    <span className="text-sm text-foreground/80 flex-1">{cpt.description}</span>
                    <span className={cn(
                      'text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full',
                      cpt.type === 'add-on'
                        ? 'bg-primary/10 text-primary'
                        : cpt.type === 'hcpcs'
                          ? 'bg-chart-3/10 text-chart-3'
                          : 'bg-secondary text-secondary-foreground'
                    )}>
                      {cpt.type}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ICD-10 Pills */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                <Pill className="h-4 w-4" />
                Medical Necessity (ICD-10)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {result.icd10_codes.map((icd) => (
                  <span
                    key={icd.code}
                    className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground"
                    title={icd.description}
                  >
                    <span className="font-mono font-semibold">{icd.code}</span>
                    <span className="hidden sm:inline text-muted-foreground">— {icd.description}</span>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Modifier Justification Accordion */}
          <Card>
            <CardContent className="pt-4 pb-2">
              <Accordion type="single" collapsible>
                <AccordionItem value="rationale" className="border-none">
                  <AccordionTrigger className="text-sm font-semibold hover:no-underline">
                    View AAPC Coding Rationale & Modifiers
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 pr-4 font-semibold text-muted-foreground">Modifier</th>
                            <th className="pb-2 pr-4 font-semibold text-muted-foreground">Description</th>
                            <th className="pb-2 font-semibold text-muted-foreground">Rationale</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {result.modifiers.map((mod) => (
                            <tr key={mod.code}>
                              <td className="py-2.5 pr-4 font-mono font-semibold">{mod.code}</td>
                              <td className="py-2.5 pr-4">{mod.description}</td>
                              <td className="py-2.5 text-muted-foreground">{mod.rationale}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* --- GRAY ZONE WARNING --- */}
          {(result.documentation_gaps.length > 0 || result.suggested_additions_to_note) && (
            <Card className="border-warning/40 bg-warning/5">
              <CardContent className="py-5 space-y-4">
                {result.documentation_gaps.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-warning" />
                      <h4 className="text-sm font-bold text-warning-foreground">
                        Warning: Missing Evidence
                      </h4>
                    </div>
                    <ul className="space-y-1 pl-7 list-disc text-sm text-foreground/80">
                      {result.documentation_gaps.map((gap, i) => (
                        <li key={i}>{gap}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.suggested_additions_to_note && (
                  <div className="space-y-2 pt-2 border-t border-warning/20">
                    <p className="text-sm font-medium text-foreground/80">
                      Suggested addition to operative note:
                    </p>
                    <blockquote className="border-l-2 border-warning/50 pl-3 text-sm italic text-muted-foreground">
                      {result.suggested_additions_to_note}
                    </blockquote>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAppendSuggestion}
                      className="border-warning/40 text-warning-foreground hover:bg-warning/10"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Append to Operative Note
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* --- TRUST & SOVEREIGNTY FOOTER --- */}
          <Card className="bg-muted/50">
            <CardContent className="py-5 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Documentation Confidence
                  </span>
                  <span className={cn('text-sm font-bold', confidenceTextColor)}>
                    {result.confidence_level} ({result.confidence_score}%)
                  </span>
                </div>
                <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    className={cn('h-full rounded-full transition-all duration-700', confidenceColor)}
                    style={{ width: `${result.confidence_score}%` }}
                  />
                </div>
              </div>

              <Phase3Disclaimer variant="compact" />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
